/**
 * SignupFooter — policy block + copyright.
 *
 * Fetches link-style consent_disclosures from the worker and renders them in
 * the order defined by FOOTER_POLICY_ORDER. Any disclosure not in the order
 * list still renders but at the end. Admin manages URLs + labels via Admin →
 * Prospects → Settings → Disclosures (per design Section 7).
 */

import { useEffect, useState } from 'react';
import { getConsentDisclosures } from '../lib/api';
import type { ConsentDisclosure } from '../lib/types';

// Preferred display order. Policies not in this list render after these,
// sorted by their text_content alphabetically.
const FOOTER_POLICY_ORDER = [
  'terms_of_service',
  'privacy_policy',
  'eula',
  'acceptable_use',
  'cookie_policy',
  'refund_policy',
  'data_processing',
  'security',
  'tcpa_compliance',
  'compliance_guide',
];

export default function SignupFooter() {
  const [disclosures, setDisclosures] = useState<ConsentDisclosure[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getConsentDisclosures();
      if (cancelled) return;
      if (res.success && res.data) setDisclosures(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter to link-style policies only (those with a URL)
  const linkPolicies = (disclosures || []).filter((d) => !!d.url);

  // Sort by preferred order
  linkPolicies.sort((a, b) => {
    const ia = FOOTER_POLICY_ORDER.indexOf(a.policy_key);
    const ib = FOOTER_POLICY_ORDER.indexOf(b.policy_key);
    if (ia === -1 && ib === -1) return a.text_content.localeCompare(b.text_content);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 mt-12">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {linkPolicies.length > 0 && (
          <nav className="flex flex-wrap gap-x-5 gap-y-2 justify-center mb-4 text-xs">
            {linkPolicies.map((p) => (
              <a
                key={p.id}
                href={p.url!}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:underline"
              >
                {p.text_content}
              </a>
            ))}
          </nav>
        )}
        <p className="text-center text-xs text-slate-400 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Oasis Auto Konnect LLC. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
